import type { FlowEventForBuilder } from '@bliks-io/ui';
import type {
  FlowPositionItem,
  ProcessDraftStep,
  ProcessWithSteps,
  StepTemplate
} from '@bliks-io/contracts';
import { draftStepToDisplay } from './draftStepToDisplay';

/**
 * Render input for a step template's thumbnail. The template-local `nodeId` is
 * the node identity the stored `inputs[]` edges reference, so it becomes the
 * flow node `_id`.
 */
export function stepTemplateToFlowProcess(template: StepTemplate): {
  process: ProcessWithSteps;
  events: FlowEventForBuilder[];
  positions: FlowPositionItem[];
  nodeIds: string[];
} {
  // A template step is a draft step with its `nodeId` standing in for
  // `stableStepId` — the display adapter already knows the rest of the shape.
  const steps = template.steps.map((step) =>
    draftStepToDisplay(
      { ...step, stableStepId: step.nodeId } as unknown as ProcessDraftStep,
      String(template._id)
    )
  );

  const events: FlowEventForBuilder[] = template.events.map((event) => ({
    _id: String(event.nodeId),
    nodeKind: 'event',
    // A saved start event comes back as intermediate — the template carries no
    // `eventKind` and a draft tolerates exactly one start event.
    eventKind: 'intermediate',
    eventType: event.eventType,
    name: event.name ?? '',
    description: event.description ?? ''
  }));

  return {
    process: {
      _id: String(template._id),
      processStepRecordings: steps,
      updatedAt: template.updatedAt
    } as unknown as ProcessWithSteps,
    events,
    positions: template.positions.map((p) => ({ stepId: String(p.nodeId), x: p.x, y: p.y })),
    nodeIds: [...steps.map((s) => s._id), ...events.map((e) => e._id)]
  };
}
